import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { PlusIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline';

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

export default function PerformanceTab({ gymId, canEdit }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ month: new Date().getMonth() + 1, year: new Date().getFullYear(), revenue: '', expenses: '', clientCount: '', notes: '' });

  useEffect(() => {
    fetchEntries();
  }, [gymId]);

  const fetchEntries = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`/api/performance/${gymId}`);
      setEntries(res.data);
    } catch {
      toast.error('Failed to load performance data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await axios.put(`/api/performance/${gymId}/${editing.id}`, form);
        toast.success('Entry updated');
      } else {
        await axios.post(`/api/performance/${gymId}`, form);
        toast.success('Entry added');
      }
      setShowForm(false);
      setEditing(null);
      setForm({ month: new Date().getMonth() + 1, year: new Date().getFullYear(), revenue: '', expenses: '', clientCount: '', notes: '' });
      fetchEntries();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save entry');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this entry?')) return;
    try {
      await axios.delete(`/api/performance/${gymId}/${id}`);
      toast.success('Entry deleted');
      fetchEntries();
    } catch {
      toast.error('Failed to delete');
    }
  };

  const totalRevenue = entries.reduce((sum, e) => sum + e.revenue, 0);
  const totalExpenses = entries.reduce((sum, e) => sum + e.expenses, 0);
  const avgClients = entries.length ? Math.round(entries.reduce((sum, e) => sum + e.clientCount, 0) / entries.length) : 0;

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6 mt-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-base font-semibold text-gray-800">Gym Performance Data</h3>
        {canEdit && (
          <button onClick={() => { setEditing(null); setShowForm(true); }}
            className="flex items-center gap-1 text-sm bg-red-600 text-white px-3 py-1.5 rounded-lg hover:bg-red-700">
            <PlusIcon className="h-4 w-4" /> Add Month
          </button>
        )}
      </div>

      {entries.length > 0 && (
        <div className="grid grid-cols-3 gap-3 mb-4">
          <SummaryCard label="Total Revenue" value={`₹${totalRevenue.toLocaleString('en-IN')}`} color="green" />
          <SummaryCard label="Total Expenses" value={`₹${totalExpenses.toLocaleString('en-IN')}`} color="red" />
          <SummaryCard label="Avg Clients/Month" value={avgClients} color="blue" />
        </div>
      )}

      {showForm && (
        <form onSubmit={handleSubmit} className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-4">
          <p className="text-sm font-medium text-gray-700 mb-3">{editing ? 'Edit Entry' : 'Add Monthly Entry'}</p>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-gray-500">Month</label>
              <select value={form.month} onChange={e => setForm(p => ({ ...p, month: parseInt(e.target.value) }))}
                className="w-full mt-1 px-2 py-1.5 text-sm border border-gray-300 rounded">
                {MONTHS.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-gray-500">Year</label>
              <input type="number" min="2020" max="2030" value={form.year}
                onChange={e => setForm(p => ({ ...p, year: parseInt(e.target.value) }))}
                className="w-full mt-1 px-2 py-1.5 text-sm border border-gray-300 rounded" />
            </div>
            <div>
              <label className="text-xs text-gray-500">Revenue (₹)</label>
              <input type="number" min="0" required value={form.revenue}
                onChange={e => setForm(p => ({ ...p, revenue: e.target.value }))}
                className="w-full mt-1 px-2 py-1.5 text-sm border border-gray-300 rounded" />
            </div>
            <div>
              <label className="text-xs text-gray-500">Expenses (₹)</label>
              <input type="number" min="0" required value={form.expenses}
                onChange={e => setForm(p => ({ ...p, expenses: e.target.value }))}
                className="w-full mt-1 px-2 py-1.5 text-sm border border-gray-300 rounded" />
            </div>
            <div>
              <label className="text-xs text-gray-500">Client Count</label>
              <input type="number" min="0" required value={form.clientCount}
                onChange={e => setForm(p => ({ ...p, clientCount: e.target.value }))}
                className="w-full mt-1 px-2 py-1.5 text-sm border border-gray-300 rounded" />
            </div>
            <div>
              <label className="text-xs text-gray-500">Notes (optional)</label>
              <input type="text" value={form.notes}
                onChange={e => setForm(p => ({ ...p, notes: e.target.value }))}
                className="w-full mt-1 px-2 py-1.5 text-sm border border-gray-300 rounded" />
            </div>
          </div>
          <div className="flex gap-2 mt-3">
            <button type="submit" className="bg-red-600 text-white text-sm px-4 py-1.5 rounded hover:bg-red-700">Save</button>
            <button type="button" onClick={() => setShowForm(false)} className="text-sm px-4 py-1.5 rounded border hover:bg-gray-50">Cancel</button>
          </div>
        </form>
      )}

      {loading ? (
        <p className="text-sm text-gray-400">Loading...</p>
      ) : entries.length === 0 ? (
        <p className="text-sm text-gray-400 italic">No performance data yet. Add the first month entry.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-2 text-xs font-semibold text-gray-500 uppercase">Month</th>
                <th className="text-right py-2 text-xs font-semibold text-gray-500 uppercase">Revenue</th>
                <th className="text-right py-2 text-xs font-semibold text-gray-500 uppercase">Expenses</th>
                <th className="text-right py-2 text-xs font-semibold text-gray-500 uppercase">Profit</th>
                <th className="text-right py-2 text-xs font-semibold text-gray-500 uppercase">Clients</th>
                <th className="py-2 text-xs font-semibold text-gray-500 uppercase">Notes</th>
                {canEdit && <th className="py-2"></th>}
              </tr>
            </thead>
            <tbody>
              {entries.map(e => {
                const profit = e.revenue - e.expenses;
                return (
                  <tr key={e.id} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-2 font-medium">{MONTHS[e.month - 1]} {e.year}</td>
                    <td className="py-2 text-right text-green-700">₹{e.revenue.toLocaleString('en-IN')}</td>
                    <td className="py-2 text-right text-red-600">₹{e.expenses.toLocaleString('en-IN')}</td>
                    <td className={`py-2 text-right font-semibold ${profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      ₹{Math.abs(profit).toLocaleString('en-IN')}{profit < 0 ? ' loss' : ''}
                    </td>
                    <td className="py-2 text-right">{e.clientCount.toLocaleString('en-IN')}</td>
                    <td className="py-2 text-gray-500 text-xs">{e.notes || '—'}</td>
                    {canEdit && (
                      <td className="py-2">
                        <div className="flex gap-2 justify-end">
                          <button onClick={() => { setEditing(e); setForm({ month: e.month, year: e.year, revenue: e.revenue, expenses: e.expenses, clientCount: e.clientCount, notes: e.notes || '' }); setShowForm(true); }}
                            className="text-gray-400 hover:text-blue-600"><PencilIcon className="h-3.5 w-3.5" /></button>
                          <button onClick={() => handleDelete(e.id)}
                            className="text-gray-400 hover:text-red-600"><TrashIcon className="h-3.5 w-3.5" /></button>
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function SummaryCard({ label, value, color }) {
  const colors = { green: 'text-green-700 bg-green-50', red: 'text-red-700 bg-red-50', blue: 'text-blue-700 bg-blue-50' };
  return (
    <div className={`rounded-lg p-3 ${colors[color]}`}>
      <p className="text-xs font-medium opacity-70">{label}</p>
      <p className="text-base font-bold mt-0.5">{value}</p>
    </div>
  );
}
